import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  ChevronDown, 
  ChevronUp,
  Box,
  Filter,
  Gauge,
  Beaker,
  Activity,
  RotateCw,
  AlertTriangle,
  Thermometer,
  Timer,
  Waves,
  Zap
} from 'lucide-react';
import { ProcessFlowDiagram } from './ProcessFlowDiagram';
import { AssetGauge } from './AssetGauge';
import { Asset } from '../types';

export const PreTreatmentStage: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);

  useEffect(() => {
    const initialAssets: Asset[] = [
      {
        id: 'mixer1',
        name: 'Rapid Mixer',
        type: 'Mechanical Mixer',
        description: 'High-speed chemical mixing unit',
        manufacturer: 'Philadelphia Mixing Solutions',
        model: 'XTR-750',
        installationDate: '2023-05-15',
        maintenanceSchedule: 'Monthly',
        status: 'operational',
        metrics: [
          {
            name: 'Mixing Speed',
            value: 120,
            unit: 'RPM',
            normalRange: { min: 100, max: 150 },
            criticalRange: { min: 80, max: 170 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 120 + Math.random() * 10 - 5
            }))
          },
          {
            name: 'Power Draw',
            value: 2.8,
            unit: 'kW',
            normalRange: { min: 2.0, max: 3.5 },
            criticalRange: { min: 1.5, max: 4.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 2.8 + Math.random() * 0.3 - 0.15
            }))
          },
          {
            name: 'Vibration',
            value: 1.2,
            unit: 'mm/s',
            normalRange: { min: 0, max: 2.5 },
            criticalRange: { min: 0, max: 3.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 1.2 + Math.random() * 0.4 - 0.2
            }))
          }
        ]
      },
      {
        id: 'coagdos1',
        name: 'Coagulant Dosing',
        type: 'Chemical Dosing System',
        description: 'Aluminum sulfate dosing system',
        manufacturer: 'ProMinent',
        model: 'Sigma X',
        installationDate: '2023-05-20',
        maintenanceSchedule: 'Weekly',
        status: 'operational',
        metrics: [
          {
            name: 'Dosing Rate',
            value: 42,
            unit: 'L/h',
            normalRange: { min: 35, max: 50 },
            criticalRange: { min: 30, max: 55 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 42 + Math.random() * 4 - 2
            }))
          },
          {
            name: 'Tank Level',
            value: 75,
            unit: '%',
            normalRange: { min: 20, max: 90 },
            criticalRange: { min: 10, max: 95 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 75 + Math.random() * 2 - 1
            }))
          },
          {
            name: 'Solution pH',
            value: 3.2,
            unit: 'pH',
            normalRange: { min: 2.8, max: 3.5 },
            criticalRange: { min: 2.5, max: 4.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 3.2 + Math.random() * 0.2 - 0.1
            }))
          }
        ]
      },
      {
        id: 'floc1',
        name: 'Flocculation Basin',
        type: 'Mechanical Flocculator',
        description: 'Three-stage tapered flocculation system',
        manufacturer: 'WesTech Engineering',
        model: 'FlowTech 3000',
        installationDate: '2023-05-25',
        maintenanceSchedule: 'Quarterly',
        status: 'warning',
        metrics: [
          {
            name: 'Paddle Speed',
            value: 35,
            unit: 'RPM',
            normalRange: { min: 25, max: 40 },
            criticalRange: { min: 20, max: 45 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 35 + Math.random() * 3 - 1.5
            }))
          },
          {
            name: 'Retention Time',
            value: 28,
            unit: 'min',
            normalRange: { min: 20, max: 35 },
            criticalRange: { min: 15, max: 40 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 28 + Math.random() * 2 - 1
            }))
          },
          {
            name: 'Turbidity',
            value: 45,
            unit: 'NTU',
            normalRange: { min: 20, max: 60 },
            criticalRange: { min: 10, max: 80 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 45 + Math.random() * 6 - 3
            }))
          }
        ]
      },
      {
        id: 'phdos1',
        name: 'pH Correction',
        type: 'Chemical Dosing System',
        description: 'Lime dosing for pH adjustment',
        manufacturer: 'Watson Marlow',
        model: 'Qdos 60',
        installationDate: '2023-06-01',
        maintenanceSchedule: 'Monthly',
        status: 'operational',
        metrics: [
          {
            name: 'Dosing Rate',
            value: 15,
            unit: 'L/h',
            normalRange: { min: 10, max: 20 },
            criticalRange: { min: 5, max: 25 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 15 + Math.random() * 2 - 1
            }))
          },
          {
            name: 'Tank Level',
            value: 65,
            unit: '%',
            normalRange: { min: 20, max: 90 },
            criticalRange: { min: 10, max: 95 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 65 + Math.random() * 2 - 1
            }))
          },
          {
            name: 'Output pH',
            value: 7.2,
            unit: 'pH',
            normalRange: { min: 6.8, max: 7.5 },
            criticalRange: { min: 6.5, max: 8.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 7.2 + Math.random() * 0.2 - 0.1
            }))
          }
        ]
      },
      {
        id: 'clarifier1',
        name: 'Clarifier',
        type: 'Sedimentation Basin',
        description: 'Circular clarifier with sludge removal',
        manufacturer: 'Evoqua',
        model: 'ClearFlow X80',
        installationDate: '2023-06-05',
        maintenanceSchedule: 'Semi-annual',
        status: 'operational',
        metrics: [
          {
            name: 'Turbidity',
            value: 2.5,
            unit: 'NTU',
            normalRange: { min: 0, max: 5 },
            criticalRange: { min: 0, max: 10 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 2.5 + Math.random() * 0.5 - 0.25
            }))
          },
          {
            name: 'Sludge Level',
            value: 35,
            unit: '%',
            normalRange: { min: 0, max: 50 },
            criticalRange: { min: 0, max: 70 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 35 + Math.random() * 4 - 2
            }))
          },
          {
            name: 'Surface Loading',
            value: 1.8,
            unit: 'm/h',
            normalRange: { min: 1.5, max: 2.5 },
            criticalRange: { min: 1.0, max: 3.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 1.8 + Math.random() * 0.2 - 0.1
            }))
          }
        ]
      }
    ];

    setAssets(initialAssets);

    const interval = setInterval(() => {
      setAssets(prevAssets => prevAssets.map(asset => ({
        ...asset,
        metrics: asset.metrics.map(metric => {
          const newValue = metric.value + (Math.random() * 2 - 1) * (metric.value * 0.02);
          return {
            ...metric,
            value: newValue,
            history: [
              ...metric.history.slice(1),
              {
                timestamp: Date.now(),
                value: newValue
              }
            ]
          };
        })
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getAssetIcon = (assetId: string) => {
    if (assetId.includes('mixer')) return <RotateCw className="h-6 w-6" />;
    if (assetId.includes('coagdos')) return <Beaker className="h-6 w-6" />;
    if (assetId.includes('floc')) return <Beaker className="h-6 w-6" />;
    if (assetId.includes('phdos')) return <Beaker className="h-6 w-6" />;
    if (assetId.includes('clarifier')) return <Box className="h-6 w-6" />;
    return <Activity className="h-6 w-6" />;
  };

  const getMetricIcon = (metricName: string) => {
    if (metricName.includes('Temperature')) return <Thermometer className="h-4 w-4" />;
    if (metricName.includes('Flow') || metricName.includes('Rate')) return <Waves className="h-4 w-4" />;
    if (metricName.includes('Time')) return <Timer className="h-4 w-4" />;
    if (metricName.includes('Power')) return <Zap className="h-4 w-4" />;
    if (metricName.includes('Level')) return <Activity className="h-4 w-4" />;
    if (metricName.includes('pH')) return <Beaker className="h-4 w-4" />;
    if (metricName.includes('Speed')) return <RotateCw className="h-4 w-4" />;
    if (metricName.includes('Turbidity')) return <Filter className="h-4 w-4" />;
    return <Gauge className="h-4 w-4" />;
  };

  const getStatusColor = (status: Asset['status']) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
    }
  };

  const isMetricInRange = (value: number, metric: Asset['metrics'][0]) => {
    if (!metric.normalRange) return true;
    return value >= metric.normalRange.min && value <= metric.normalRange.max;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-6">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 bg-indigo-600 text-white flex items-center justify-between hover:bg-indigo-700 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Beaker className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Stage 2 - Pre-Treatment</h2>
        </div>
        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4">
          {/* Process Overview */}
          <div className="mb-6">
            <ProcessFlowDiagram assets={assets} />
          </div>

          {/* Key Metrics Gauges */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {assets.map(asset => 
              asset.metrics.slice(0, 1).map(metric => (
                <div key={`${asset.id}-${metric.name}`} className="bg-white rounded-lg shadow p-4">
                  <AssetGauge
                    value={metric.value}
                    min={metric.normalRange?.min || 0}
                    max={metric.normalRange?.max || 100}
                    unit={metric.unit}
                    title={`${asset.name} - ${metric.name}`}
                  />
                </div>
              ))
            )}
          </div>

          {/* Asset Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => (
              <div
                key={asset.id}
                onClick={() => setSelectedAsset(asset.id === selectedAsset ? null : asset.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  asset.id === selectedAsset
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {getAssetIcon(asset.id)}
                    <div>
                      <h3 className="font-medium">{asset.name}</h3>
                      <p className="text-sm text-gray-500">{asset.type}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                    {asset.status}
                  </span>
                </div>
                <div className="space-y-2">
                  {asset.metrics.map(metric => (
                    <div key={metric.name} className="flex justify-between items-baseline">
                      <div className="flex items-center space-x-2">
                        {getMetricIcon(metric.name)}
                        <span className="text-sm text-gray-500">{metric.name}</span>
                      </div>
                      <span className={`text-lg font-semibold ${
                        isMetricInRange(metric.value, metric) 
                          ? 'text-gray-900' 
                          : 'text-red-600'
                      }`}>
                        {metric.value.toFixed(1)} {metric.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};